import { useOrganizationList, useUser } from '@clerk/nextjs';
import type { UserOrganizationInvitationResource } from '@clerk/types';
import { V2 } from '@firmable/common';
import { Logo, Update } from '@firmable/icons';
import { Routes } from '@web/constants/routes';
import { useUnlinkedStatus } from '@web/query/App/Onboarding/useUnlinkedStatus';
import { useRouter } from 'next/router';
import React from 'react';
import { AccessRemoved } from '../AccessRemoved/AccessRemoved';
import { CreateCompanyForm } from '../CreateCompanyForm';
import { InvitationCard } from '../InvitationCard';
import { CreateCompanyFooter, OrgAvatar } from '../shared';

const RequestSentBanner = ({ orgName }: { orgName: string }) => (
  <div className="bg-success-lighter rounded-lg border border-success-default flex p-lg items-center gap-sm">
    <Update height={20} width={20} className="text-success-dark shrink-0" />
    <p className="text-success-dark text-center text-label-large">
      Your request to join {orgName} has been sent. A Firmable admin from {orgName} will review your request.
    </p>
  </div>
);

export const OrganizationSelector = () => {
  const router = useRouter();
  const { user } = useUser();
  const { isLoaded, userSuggestions, userInvitations, setActive } = useOrganizationList({
    userSuggestions: { status: ['pending', 'accepted'] },
    userInvitations: {},
  });
  const [view, setView] = React.useState<'suggestions' | 'create'>('suggestions');
  const [joiningId, setJoiningId] = React.useState<string | null>(null);
  const [acceptingInvitation, setAcceptingInvitation] = React.useState(false);
  const [invitationDismissed, setInvitationDismissed] = React.useState(false);

  const unlinkedQuery = useUnlinkedStatus();

  const emailDomain = user?.primaryEmailAddress?.emailAddress?.split('@')[1] ?? '';

  const isDataLoading =
    !isLoaded || userSuggestions?.isLoading || userInvitations?.isLoading || unlinkedQuery.isLoading;

  if (isDataLoading || acceptingInvitation) {
    return null;
  }

  const isUnlinked = unlinkedQuery.data?.isUnlinked === true;

  const hasSuggestions = (userSuggestions?.data?.length ?? 0) > 0;
  const hasInvitations = (userInvitations?.data?.length ?? 0) > 0;

  // No suggestions or invitations → create company form (no back button)
  if (!hasSuggestions && !hasInvitations) {
    return isUnlinked ? <AccessRemoved /> : <CreateCompanyForm />;
  }

  // Create company sub-view (with back button)
  if (view === 'create') {
    return isUnlinked ? <AccessRemoved /> : <CreateCompanyForm onBack={() => setView('suggestions')} />;
  }

  const handleAcceptInvitation = async (invitation: UserOrganizationInvitationResource) => {
    setAcceptingInvitation(true);
    await invitation.accept();
    if (setActive) {
      await setActive({ organization: invitation.publicOrganizationData.id });
      router.push(Routes.dashboardRoot);
    }
  };

  // Pending invitation view
  const pendingInvitation = userInvitations?.data?.find((i) => i.status === 'pending');
  if (pendingInvitation && !invitationDismissed) {
    const invitation = pendingInvitation;
    return (
      <InvitationCard
        invitation={invitation}
        onAccept={handleAcceptInvitation}
        onError={() => setInvitationDismissed(true)}
        onCreateCompany={() => setView('create')}
      />
    );
  }

  const handleJoin = async (suggestionId: string, organizationId: string) => {
    setJoiningId(suggestionId);

    const suggestion = userSuggestions?.data?.find((s) => s.id === suggestionId);
    if (!suggestion) return;

    try {
      await suggestion.accept();

      // If auto-join is enabled, setActive succeeds and we redirect to dashboard.
      // If admin approval is required, setActive throws — we show "Requested".
      if (setActive) {
        await setActive({ organization: organizationId });
        router.push(Routes.dashboardRoot);
        return;
      }
    } catch {
      // setActive failed → membership requires admin approval
    }

    setJoiningId(null);
  };

  const suggestion = userSuggestions?.data?.[0];
  if (!suggestion) return isUnlinked ? <AccessRemoved /> : <CreateCompanyForm />;

  const orgName = suggestion.publicOrganizationData.name;
  const isAccepted = suggestion.status === 'accepted';

  const subtitle = () => {
    if (isAccepted) return <RequestSentBanner orgName={orgName} />;
    if (!hasInvitations)
      return (
        <p className="text-sm text-neutral-500">
          We found {orgName} for your email domain @{emailDomain}
        </p>
      );
    return null;
  };

  return (
    <div className="bg-neutral-50 flex flex-col gap-6 items-center w-[400px] sm:w-[440px] px-6 sm:px-10 py-8 rounded-2xl">
      <Logo.Light.FirmableFull height={30} width={142} />

      <div className="flex flex-col gap-2 items-center text-center w-full">
        <OrgAvatar org={suggestion.publicOrganizationData} />
        <p className="font-semibold text-base text-black">{orgName}</p>
        {subtitle()}
      </div>

      {isAccepted ? (
        <V2.Button
          variant="primary"
          size="lg"
          disabled
          className="!w-full !h-11 !bg-brand-1-600 !text-white !font-semibold !text-base !rounded-lg shadow-btn-bevel opacity-50">
          Requested
        </V2.Button>
      ) : (
        <V2.Button
          variant="primary"
          size="lg"
          disabled={joiningId !== null}
          onClick={() => handleJoin(suggestion.id, suggestion.publicOrganizationData.id)}
          className="!w-full !h-11 !bg-brand-1-600 hover:!bg-brand-1-700 !text-white !font-semibold !text-base !rounded-lg shadow-btn-bevel">
          {joiningId === suggestion.id ? 'Requesting...' : 'Request to join'}
        </V2.Button>
      )}

      {isUnlinked ? null : <CreateCompanyFooter onClick={() => setView('create')} />}
    </div>
  );
};
